import { z } from 'zod';

// Defining the innermost nested schemas first
const B2CProductFormulationSchema = z.object({
  id: z.number(),
  name: z.string(),
});
type B2CProductFormulationType = z.infer<typeof B2CProductFormulationSchema>;

const B2CApprovalTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
});
type B2CApprovalTypeType = z.infer<typeof B2CApprovalTypeSchema>;

const B2CProductCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
});
type B2CProductCategoryType = z.infer<typeof B2CProductCategorySchema>;

const B2CApprovalStatusSchema = z.object({
  id: z.number(),
  name: z.string(),
});
type B2CApprovalStatusType = z.infer<typeof B2CApprovalStatusSchema>;

const B2CApprovalDocumentSchema = z
  .object({
    id: z.number(),
    approval_code: z.string(),
    doctor_name: z.string(),
    document_path: z.string(),
    approval_status_id: z.number(),
    category_id: z.number(),
    approval_type_id: z.number(),
    formulation_id: z.number(),
    ApprovalStatus: B2CApprovalStatusSchema,
    ProductCategory: B2CProductCategorySchema,
    ApprovalType: B2CApprovalTypeSchema,
    ProductFormulation: B2CProductFormulationSchema,
  })
  .nullable();
type B2CApprovalDocumentType = z.infer<typeof B2CApprovalDocumentSchema>;

const B2CPharmacySchema = z.object({
  name: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  phone: z.string().nullable(),
});
type B2CPharmacyType = z.infer<typeof B2CPharmacySchema>;

const B2CPharmacistSchema = z.object({
  tag: z.string(),
  id: z.number(),
  Pharmacy: B2CPharmacySchema,
});
type B2CPharmacistType = z.infer<typeof B2CPharmacistSchema>;

const B2COrderSchema = z.object({
  id: z.number(),
  order_date: z.string(),
  Pharmacist: B2CPharmacistSchema,
});
type B2COrderType = z.infer<typeof B2COrderSchema>;

const B2CProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  supplier_name: z.string(),
  sku_code: z.string(),
  formulation_id: z.number(),
});
type B2CProductType = z.infer<typeof B2CProductSchema>;

// Copied these statuses from the old classic app, not sure if they will all be used. Just trying to account for all possibilities
const B2COrderItemStatusSchema = z.union([
  z.object({
    id: z.literal(1),
    name: z.literal('Awaiting Approval'),
  }),
  z.object({
    id: z.literal(2),
    name: z.literal('Awaiting Shipment'),
  }),
  z.object({
    id: z.literal(3),
    name: z.literal('Shipped'),
  }),
  z.object({
    id: z.literal(4),
    name: z.literal('Completed'),
  }),
  z.object({
    id: z.literal(5),
    name: z.literal('Canceled'),
  }),
  z.object({
    id: z.literal(6),
    name: z.literal('Approval Rejected'),
  }),
  z.object({
    id: z.literal(7),
    name: z.literal('Approval Expired'),
  }),
  z.object({
    id: z.literal(8),
    name: z.literal('Shipped with dispute'),
  }),
  z.object({
    id: z.literal(9),
    name: z.literal('Warehouse Shipment'),
  }),
  z.object({
    id: z.literal(10),
    name: z.literal('Pending Approval'),
  }),
  z.object({
    id: z.literal(11),
    name: z.literal('Awaiting approval - Max Qty exceeded'),
  }),
  z.object({
    id: z.literal(12),
    name: z.literal('Processing in warehouse'),
  }),
  z.object({
    id: z.literal(13),
    name: z.literal('Deleted'),
  }),
  z.object({
    id: z.literal(14),
    name: z.literal('Awaiting Supplier Approval'),
  }),
  z.object({
    id: z.literal(15),
    name: z.literal('Rejected by Supplier'),
  }),
]);
type B2COrderItemStatusType = z.infer<typeof B2COrderItemStatusSchema>;

const B2CConsignmentSchema = z.object({
  shipped_date: z.string().nullable(),
  tag: z.string(),
});
type B2CConsignmentType = z.infer<typeof B2CConsignmentSchema>;

const B2CConsignmentOrdersItemSchema = z.object({
  order_item_id: z.number(),
  Consignment: B2CConsignmentSchema,
});
type B2CConsignmentOrdersItemType = z.infer<typeof B2CConsignmentOrdersItemSchema>;

// Main list item schema
const B2CListItemSchema = z.object({
  id: z.number(),
  created_date: z.string(),
  quantity: z.number(),
  item_status_id: z.number(),
  Order: B2COrderSchema,
  ApprovalDocument: B2CApprovalDocumentSchema,
  Product: B2CProductSchema,
  OrderItemStatus: B2COrderItemStatusSchema,
  ConsignmentOrdersItem: B2CConsignmentOrdersItemSchema.nullable(),
});
type B2CListItemType = z.infer<typeof B2CListItemSchema>;

// Pagination schema
const B2CPaginationSchema = z.object({
  total: z.number(),
  perpage: z.number(),
  currentPage: z.number(),
  lastPage: z.number(),
  from: z.number(),
  to: z.number(),
  loadMore: z.boolean(),
});
type B2CPaginationType = z.infer<typeof B2CPaginationSchema>;

// Complete data schema
const B2CDataSchema = z.object({
  pagination: B2CPaginationSchema,
  list: z.array(B2CListItemSchema),
});
type B2CDataType = z.infer<typeof B2CDataSchema>;

// Full API response schema
const B2CApiResponseSchema = z.object({
  success: z.boolean(),
  statusCode: z.number(),
  data: B2CDataSchema,
  message: z.string(),
});
type B2CApiResponseType = z.infer<typeof B2CApiResponseSchema>;

export {
  B2CApiResponseSchema,
  B2CApprovalDocumentSchema,
  B2CApprovalStatusSchema,
  B2CApprovalTypeSchema,
  B2CConsignmentOrdersItemSchema,
  B2CConsignmentSchema,
  B2CDataSchema,
  B2CListItemSchema,
  B2COrderItemStatusSchema,
  B2COrderSchema,
  B2CPaginationSchema,
  B2CPharmacistSchema,
  B2CPharmacySchema,
  B2CProductCategorySchema,
  B2CProductFormulationSchema,
  B2CProductSchema,
};

export type {
  B2CApiResponseType,
  B2CApprovalDocumentType,
  B2CApprovalStatusType,
  B2CApprovalTypeType,
  B2CConsignmentOrdersItemType,
  B2CConsignmentType,
  B2CDataType,
  B2CListItemType,
  B2COrderItemStatusType,
  B2COrderType,
  B2CPaginationType,
  B2CPharmacistType,
  B2CPharmacyType,
  B2CProductCategoryType,
  B2CProductFormulationType,
  B2CProductType,
};
